/**
 * POS receipt payload builders — works fully offline from local ticket data (plan/28).
 */

import { d, money } from "@icraftx/shared/v1";

export type PosReceiptLine = {
  name: string;
  qty: string;
  unitPrice: string;
  lineTotal: string;
};

export type PosReceiptData = {
  companyName: string;
  terminalName: string;
  number: string;
  /** True when number is device-local until sync assigns server series */
  provisional: boolean;
  issuedAt: string;
  lines: PosReceiptLine[];
  discountPct: string | null;
  netTotal: string;
  taxTotal: string;
  grossTotal: string;
  taxPriceMode: "inclusive" | "exclusive";
  payMethodName: string;
  tenderAmount: string;
  changeGiven: string;
  currencyCode: string;
};

export function lineGross(unitPrice: string | number, qty: string | number): string {
  return money(d(unitPrice).mul(d(qty)));
}

/** Short local number until server series applies — never invents SO/POS seq. */
export function provisionalTicketNumber(ticketId: string): string {
  const compact = ticketId.replace(/-/g, "").slice(0, 8).toUpperCase();
  return `LOCAL-${compact}`;
}

export function changeFromTender(
  tenderAmount: string | number,
  grossTotal: string | number,
): string {
  const change = d(tenderAmount).sub(d(grossTotal));
  return money(change.lt(0) ? 0 : change);
}

/** Split tax from tax-inclusive gross (org default path). */
export function splitInclusiveTax(
  gross: string | number | ReturnType<typeof d>,
  taxRatePct: string | number | ReturnType<typeof d>,
): { net: string; tax: string; gross: string } {
  const g = d(gross);
  const rate = d(taxRatePct);
  if (rate.lte(0)) {
    return { net: money(g), tax: money(0), gross: money(g) };
  }
  const net = g.mul(100).div(d(100).add(rate));
  const tax = g.sub(net);
  return { net: money(net), tax: money(tax), gross: money(g) };
}

export function buildReceipt(input: {
  companyName: string;
  terminalName: string;
  number: string;
  provisional?: boolean;
  issuedAt?: string;
  lines: Array<{ name: string; qty: string | number; unitPrice: string | number }>;
  discountPct?: string | null;
  taxRatePct: string | number;
  taxPriceMode: "inclusive" | "exclusive";
  payMethodName: string;
  tenderAmount: string | number;
  /** Server totals when known; else derived from lines */
  netTotal?: string;
  taxTotal?: string;
  grossTotal?: string;
  changeGiven?: string;
  currencyCode?: string;
}): PosReceiptData {
  const receiptLines: PosReceiptLine[] = input.lines.map((l) => ({
    name: l.name,
    qty: String(l.qty),
    unitPrice: money(l.unitPrice),
    lineTotal: lineGross(l.unitPrice, l.qty),
  }));

  let gross = receiptLines.reduce((acc, l) => acc.add(d(l.lineTotal)), d(0));
  const discPct = input.discountPct?.trim() ? d(input.discountPct) : d(0);
  if (discPct.gt(0)) {
    gross = gross.mul(d(100).sub(discPct)).div(100);
  }

  let netTotal: string;
  let taxTotal: string;
  let grossTotal: string;

  if (input.grossTotal != null && input.netTotal != null && input.taxTotal != null) {
    netTotal = money(input.netTotal);
    taxTotal = money(input.taxTotal);
    grossTotal = money(input.grossTotal);
  } else if (input.taxPriceMode === "inclusive") {
    const split = splitInclusiveTax(gross, input.taxRatePct);
    netTotal = split.net;
    taxTotal = split.tax;
    grossTotal = split.gross;
  } else {
    const tax = gross.mul(d(input.taxRatePct)).div(100);
    netTotal = money(gross);
    taxTotal = money(tax);
    grossTotal = money(gross.add(tax));
  }

  const tender = money(input.tenderAmount);
  const change =
    input.changeGiven != null
      ? money(input.changeGiven)
      : changeFromTender(tender, grossTotal);

  return {
    companyName: input.companyName,
    terminalName: input.terminalName,
    number: input.number,
    provisional: input.provisional ?? false,
    issuedAt: input.issuedAt ?? new Date().toISOString(),
    lines: receiptLines,
    discountPct: discPct.gt(0) ? String(input.discountPct) : null,
    netTotal,
    taxTotal,
    grossTotal,
    taxPriceMode: input.taxPriceMode,
    payMethodName: input.payMethodName,
    tenderAmount: tender,
    changeGiven: change,
    currencyCode: input.currencyCode ?? "PHP",
  };
}

export function formatReceiptIssuedAt(iso: string): string {
  return new Date(iso).toLocaleString("en-PH", {
    timeZone: "Asia/Manila",
    dateStyle: "medium",
    timeStyle: "short",
  });
}
